import { useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useClientData } from '../ClientLayout.jsx';
import { useAuth } from '../../auth/AuthContext.jsx';
import { api } from '../../lib/api.js';
import { AMOUNT_OWED_TIP } from '../../lib/disputeTypes.js';
import DisputeTypeSelect from '../DisputeTypeSelect.jsx';
import FieldTip from '../FieldTip.jsx';
import AddressAutocomplete from '../AddressAutocomplete.jsx';
import EvidenceUpload from '../EvidenceUpload.jsx';
import {
  mergeEvidenceFileNames,
  pendingEvidenceUrl,
} from '../../lib/evidenceFiles.js';
import {
  INTAKE_EVIDENCE_OWNER,
  copyUploadBlobs,
  deleteUploadBlobs,
  putUploadBlobs,
} from '../../lib/uploadBlobStore.js';
import {
  intakePatchFromExtraction,
} from '../../lib/evidenceExtract.js';
import { registerJetIntakeHandler } from '../../lib/jetIntakeBridge.js';
import { resolveCreateDemandId } from '../../lib/demandId.js';
import {
  duplicateInvoiceDemandMessage,
  findDemandWithInvoiceId,
} from '../../lib/invoiceDemand.js';
import { useSaveToast } from '../SaveToast.jsx';
import {
  formatDemandFeePrice,
  getDemandFeeEntitlement,
  getLocalSettings,
  normalizeDemandDefaults,
} from '../../lib/settingsStore.js';
import {
  MATTER_CURRENCIES,
  currencyDisplaySymbol,
  normalizeMatterCurrency,
} from '../../lib/matterCurrency.js';
import {
  canUploadEvidence,
  estimateFileBytes,
  evidenceVaultLimitMessage,
  evidenceVaultWarnMessage,
} from '../../lib/evidenceVault.js';
import {
  commitCurrencyAmount,
  formatCurrencyInputDisplay,
  isValidCurrencyAmount,
  parseCurrencyInputValue,
} from '../../lib/currencyInput.js';
import {
  isValidPostalForCountry,
  normalizePostalCode,
  postalCodeErrorMessage,
} from '../../lib/postalCode.js';
import {
  PAYMENT_PLAN_HINT,
  buildInstallmentPlanOptions,
  parseInstallmentSelection,
} from '../../lib/installmentPlans.js';
import {
  SETTLEMENT_CHOICE_HINT,
  SETTLEMENT_CHOICES,
  normalizeSettlementChoice,
} from '../../lib/settlementChoice.js';
import {
  debtorDisplayName,
  debtorIdentityErrorMessage,
  normalizeDebtorIdentity,
} from '../../lib/debtorIdentity.js';
import {
  DEBTOR_COUNTRIES,
  coerceDebtorCountry,
  debtorPostalPlaceholder,
  debtorRegionLabel,
  debtorRegionMaxLength,
  debtorRegionPlaceholder,
  normalizeDebtorCountry,
} from '../../lib/debtorCountry.js';
import {
  intakeFormFromDebtor,
  intakePrefillLabel,
  buildIntakeFormFromDebtorPrefill,
} from '../../lib/intakePrefill.js';

const EMPTY = {
  debtorName: '',
  debtorCompany: '',
  debtorCountry: 'US',
  debtorStreet: '',
  debtorCity: '',
  debtorState: '',
  debtorPostalCode: '',
  debtorEmail: '',
  amountOwed: '',
  currency: 'USD',
  externalInvoiceId: '',
  externalAccountNumber: '',
  disputeType: '',
  installmentSelection: 'NO',
  settlementChoice: 'NO',
  evidenceFileNames: [],
};

function choiceDefaults(email) {
  return normalizeDemandDefaults(getLocalSettings(email || '').demandDefaults);
}

function blankForm(email, accountCurrency = 'USD') {
  const choices = choiceDefaults(email);
  return {
    ...EMPTY,
    currency: normalizeMatterCurrency(accountCurrency),
    installmentSelection: choices.installmentPlan,
    settlementChoice: choices.settlementChoice,
  };
}

function formFromPrefill(prefill, accountCurrency = 'USD', email = '') {
  const base = blankForm(email, accountCurrency);
  if (!prefill) return base;
  const merged = buildIntakeFormFromDebtorPrefill(base, prefill);
  return {
    ...merged,
    amountOwed: '',
    currency: normalizeMatterCurrency(prefill.currency || accountCurrency),
    externalInvoiceId: '',
    externalAccountNumber: merged.externalAccountNumber,
    disputeType: '',
    installmentSelection: base.installmentSelection,
    settlementChoice: base.settlementChoice,
    evidenceFileNames: [],
  };
}

export default function IntakePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { onIntakeSubmitted, vaultUsage, debtors, allMatters } = useClientData();
  const accountCurrency = normalizeMatterCurrency(
    getLocalSettings(user?.email || '').currency || user?.currency || 'USD'
  );
  const initialPrefill = location.state?.debtorPrefill || null;
  const [form, setForm] = useState(() =>
    formFromPrefill(initialPrefill, accountCurrency, user?.email || '')
  );
  const [selectedDebtorId, setSelectedDebtorId] = useState(
    () => location.state?.debtorId || ''
  );
  const [prefillLabel, setPrefillLabel] = useState(
    () => location.state?.debtorLabel || ''
  );
  const [error, setError] = useState('');
  const [pending, startTransition] = useTransition();
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const { flashSave } = useSaveToast();
  const jetApplyRef = useRef(null);

  const planOptions = useMemo(() => buildInstallmentPlanOptions(), []);
  const demandFee = useMemo(() => {
    const billing = getLocalSettings(user?.email || '').billing || {};
    return getDemandFeeEntitlement(billing);
  }, [user?.email]);
  const demandFeeNote = `We'll pre-authorize your card for ${formatDemandFeePrice(demandFee.perDemandPrice)} (no charge yet; ${demandFee.tierLabel} rate). Pre-authorization must succeed for the demand to be sent. You're only charged when an attorney accepts—if your customer pays first, we waive the fee.`;

  const debtorOptions = useMemo(
    () =>
      (debtors || [])
        .map((d) => ({
          id: String(d.id),
          label: debtorDisplayName({
            Debtor_Name: d.name,
            Debtor_Company: d.company,
          }) || d.company || d.name || 'Debtor',
          meta: [d.company && d.company !== d.name ? d.company : null, d.state]
            .filter(Boolean)
            .join(' · '),
          record: d,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [debtors]
  );

  const contactLocked = Boolean(selectedDebtorId);

  useEffect(() => {
    const prefill = location.state?.debtorPrefill;
    if (!prefill) return;
    setForm(formFromPrefill(prefill, accountCurrency, user?.email || ''));
    setSelectedDebtorId(location.state?.debtorId || '');
    setPrefillLabel(location.state?.debtorLabel || intakePrefillLabel(prefill));
  }, [location.state, accountCurrency, user?.email]);

  useEffect(() => {
    return registerJetIntakeHandler({
      applyExtraction(payload) {
        return jetApplyRef.current?.(payload) !== false;
      },
    });
  }, []);

  function applyDebtorSelection(debtorId) {
    const id = String(debtorId || '');
    if (!id) {
      setSelectedDebtorId('');
      setPrefillLabel('');
      setForm((prev) => ({
        ...EMPTY,
        currency: prev.currency || accountCurrency,
        amountOwed: prev.amountOwed,
        externalInvoiceId: prev.externalInvoiceId,
        externalAccountNumber: prev.externalAccountNumber,
        disputeType: prev.disputeType,
        installmentSelection: prev.installmentSelection,
        settlementChoice: prev.settlementChoice,
        evidenceFileNames: prev.evidenceFileNames,
      }));
      return;
    }
    const match = debtorOptions.find((d) => d.id === id);
    if (!match) return;
    const patch = intakeFormFromDebtor(match.record);
    if (!patch) return;
    setSelectedDebtorId(id);
    setPrefillLabel(match.label);
    setForm((prev) => ({
      ...prev,
      ...patch,
    }));
  }

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function onEvidenceFiles(files) {
    const billing = getLocalSettings(user?.email || '').billing || {};
    const accepted = Array.from(files || []).filter(Boolean);
    const addBytes = accepted.reduce(
      (sum, file) => sum + estimateFileBytes(file?.name, file?.size),
      0
    );
    const gate = canUploadEvidence({
      usedBytes: vaultUsage?.usedBytes || 0,
      addBytes,
      billing,
    });
    if (!gate.ok) {
      setError(evidenceVaultLimitMessage(gate.usage));
      return false;
    }
    setEvidenceFiles((prev) => {
      const names = mergeEvidenceFileNames(
        prev.map((file) => file.name),
        accepted
      );
      const byName = new Map(
        [...prev, ...accepted].map((file) => [String(file.name).toLowerCase(), file])
      );
      return names.map((name) => byName.get(String(name).toLowerCase())).filter(Boolean);
    });
    void putUploadBlobs('evidence', INTAKE_EVIDENCE_OWNER, accepted);
    setForm((prev) => ({
      ...prev,
      evidenceFileNames: mergeEvidenceFileNames(prev.evidenceFileNames, accepted),
    }));
    return true;
  }

  jetApplyRef.current = ({ fields, files } = {}) => {
    const accepted = Array.from(files || []).filter(Boolean);
    if (accepted.length) {
      const ok = onEvidenceFiles(accepted);
      if (ok === false) return false;
    }
    const patch = intakePatchFromExtraction(fields || {});
    setForm((prev) => ({ ...prev, ...patch }));
    flashSave('Jet applied evidence fields. Review before creating the demand.');
    return true;
  };

  function removeEvidenceFile(name) {
    const target = String(name || '').toLowerCase();
    setEvidenceFiles((prev) =>
      prev.filter((file) => String(file?.name || '').toLowerCase() !== target)
    );
    void deleteUploadBlobs('evidence', INTAKE_EVIDENCE_OWNER, [name]);
    setForm((prev) => ({
      ...prev,
      evidenceFileNames: prev.evidenceFileNames.filter((item) => item !== name),
    }));
  }

  function cancel() {
    const from = location.state?.from;
    if (
      typeof from === 'string' &&
      from.startsWith('/client/') &&
      !from.startsWith('/client/demands/new')
    ) {
      navigate(from);
      return;
    }
    navigate('/client/demands');
  }

  function submit(e) {
    e.preventDefault();
    setError('');
    if (!isValidCurrencyAmount(form.amountOwed)) {
      setError('Enter a valid amount owed with up to 2 decimal places.');
      return;
    }
    const postal = normalizePostalCode(form.debtorPostalCode);
    const country = normalizeDebtorCountry(form.debtorCountry, {
      postalCode: postal,
      state: form.debtorState,
    });
    if (!isValidPostalForCountry(postal, country)) {
      setError(postalCodeErrorMessage(postal, country));
      return;
    }
    if (!form.disputeType) {
      setError('Please select a dispute type.');
      return;
    }
    const duplicateInvoice = findDemandWithInvoiceId(allMatters, form.externalInvoiceId);
    if (duplicateInvoice) {
      setError(duplicateInvoiceDemandMessage(form.externalInvoiceId, duplicateInvoice));
      return;
    }

    const debtor = normalizeDebtorIdentity({
      contactName: form.debtorName,
      company: form.debtorCompany,
    });
    if (!debtor.ok) {
      setError(debtorIdentityErrorMessage());
      return;
    }

    const plan = parseInstallmentSelection(form.installmentSelection);
    const settlementChoice = normalizeSettlementChoice(form.settlementChoice);

    startTransition(async () => {
      const demandId = resolveCreateDemandId();
      const evidenceNames = [...(form.evidenceFileNames || [])];
      const payload = {
        Debtor_Name: debtor.Debtor_Name,
        Debtor_Company: debtor.Debtor_Company,
        Debtor_Country: country,
        Debtor_Street: form.debtorStreet || null,
        Debtor_City: form.debtorCity || null,
        Debtor_State: form.debtorState.toUpperCase(),
        Debtor_Postal_Code: postal || null,
        Debtor_Email: form.debtorEmail || null,
        Amount_Owed: Number(form.amountOwed),
        Currency: normalizeMatterCurrency(form.currency, accountCurrency),
        External_Invoice_ID: form.externalInvoiceId || null,
        External_Account_Number: form.externalAccountNumber || null,
        Demand_ID: demandId,
        Dispute_Type: form.disputeType || null,
        Dispute_Other: null,
        Origin: 'MANUAL',
        Entry_Tag: 'Manual',
        Installment_Plan: plan.installmentPlan,
        Payment_Structure: plan.paymentStructure,
        Payment_Term_ID: plan.paymentTermId,
        Settlement_Choice: settlementChoice,
        Evidence_Vault_URL: pendingEvidenceUrl(evidenceNames),
        confirmPayment: false,
      };

      async function finishWithMatter(result) {
        const matterId = String(result?.matter?.ROWID || '').trim();
        if (matterId && evidenceNames.length) {
          try {
            await copyUploadBlobs(
              'evidence',
              INTAKE_EVIDENCE_OWNER,
              matterId,
              evidenceNames
            );
            await deleteUploadBlobs('evidence', INTAKE_EVIDENCE_OWNER, evidenceNames);
          } catch {
            // Local preview blobs are best-effort.
          }
        }
        onIntakeSubmitted(result);
        setEvidenceFiles([]);
        setForm(blankForm(user?.email || '', accountCurrency));
      }

      try {
        const data = await api.post('/server/client_api/submitCase', payload);
        const matter = data?.matter || {};
        await finishWithMatter({
          ...data,
          matter: {
            ...matter,
            Settlement_Choice:
              matter.Settlement_Choice != null && matter.Settlement_Choice !== ''
                ? matter.Settlement_Choice
                : settlementChoice,
          },
        });
      } catch (err) {
        setError(err?.message || 'Could not save this demand. Please try again.');
      }
    });
  }

  return (
    <div className="client-page intake-page">
      <div className="section-head">
        <div>
          <h1>Create Demand</h1>
          <p>
            {contactLocked
              ? `Contact details are filled from ${prefillLabel || 'the selected debtor'}. Add invoice details to open another demand for them. Fields lock permanently once an attorney is assigned.`
              : 'Enter debtor and invoice details to start a manual demand. Or pick an existing debtor to reuse their contact details. Fields lock permanently once an attorney is assigned.'}
          </p>
        </div>
        <div className="section-head-actions">
          <Link className="btn-ghost" to="/client/demands">
            Back to Demands
          </Link>
        </div>
      </div>

      <div className="intake-page-card">
        <form onSubmit={submit} className="intake-form intake-page-form">
          <div className="settings-grid">
            <label className="field field-span-2">
              <span>Existing debtor</span>
              <select
                value={selectedDebtorId}
                onChange={(e) => applyDebtorSelection(e.target.value)}
                aria-label="Use an existing debtor contact"
              >
                <option value="">Enter a new debtor</option>
                {debtorOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                    {opt.meta ? ` · ${opt.meta}` : ''}
                  </option>
                ))}
              </select>
              <span className="field-hint">
                Choosing a debtor prefills company, contact, address, and a saved account
                number.
              </span>
            </label>
            {contactLocked ? (
              <p className="inline-notice field-span-2">
                Using saved contact for <strong>{prefillLabel}</strong>. Clear the selection
                above to enter a different debtor.
              </p>
            ) : null}
            <label className="field">
              <span>Company</span>
              <input
                value={form.debtorCompany}
                onChange={(e) => update('debtorCompany', e.target.value)}
                placeholder="Acme LLC"
                autoComplete="organization"
                disabled={contactLocked}
              />
            </label>
            <label className="field">
              <span>Contact name</span>
              <input
                value={form.debtorName}
                onChange={(e) => update('debtorName', e.target.value)}
                placeholder="If known"
                autoComplete="name"
                disabled={contactLocked}
              />
            </label>
            <label className="field field-span-2">
              <span>Debtor street</span>
              <AddressAutocomplete
                value={form.debtorStreet}
                onChange={(next) => update('debtorStreet', next)}
                country={form.debtorCountry}
                disabled={contactLocked}
                onAddressSelect={(address) => {
                  if (contactLocked) return;
                  const nextCountry = coerceDebtorCountry(address.country);
                  setForm((prev) => ({
                    ...prev,
                    debtorStreet: address.street || prev.debtorStreet,
                    debtorCity: address.city || prev.debtorCity,
                    debtorState: address.state || prev.debtorState,
                    debtorPostalCode: address.postalCode || prev.debtorPostalCode,
                    debtorCountry: nextCountry || prev.debtorCountry,
                  }));
                }}
                placeholder="123 Main St"
              />
            </label>
            <label className="field">
              <span>Debtor city</span>
              <input
                value={form.debtorCity}
                onChange={(e) => update('debtorCity', e.target.value)}
                placeholder="Austin"
                autoComplete="address-level2"
                disabled={contactLocked}
              />
            </label>
            <label className="field">
              <span>
                {debtorRegionLabel(form.debtorCountry)}
              </span>
              <input
                required
                maxLength={debtorRegionMaxLength(form.debtorCountry)}
                value={form.debtorState}
                onChange={(e) =>
                  update(
                    'debtorState',
                    debtorRegionMaxLength(form.debtorCountry) === 2
                      ? e.target.value.toUpperCase()
                      : e.target.value
                  )
                }
                placeholder={debtorRegionPlaceholder(form.debtorCountry)}
                autoComplete="address-level1"
                disabled={contactLocked}
              />
            </label>
            <label className="field">
              <span>Debtor postal code</span>
              <input
                value={form.debtorPostalCode}
                onChange={(e) => update('debtorPostalCode', e.target.value)}
                placeholder={debtorPostalPlaceholder(form.debtorCountry)}
                autoComplete="postal-code"
                disabled={contactLocked}
              />
            </label>
            <label className="field">
              <span>Country</span>
              <select
                value={form.debtorCountry}
                onChange={(e) => update('debtorCountry', e.target.value)}
                disabled={contactLocked}
              >
                {DEBTOR_COUNTRIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Debtor email</span>
              <input
                type="email"
                value={form.debtorEmail}
                onChange={(e) => update('debtorEmail', e.target.value)}
                placeholder="billing@acme.com"
                autoComplete="email"
                disabled={contactLocked}
              />
            </label>
            <label className="field">
              <span className="field-label-with-tip">
                Amount owed
                <FieldTip
                  text={`${AMOUNT_OWED_TIP} Currency defaults from your account; change it when collecting in another country’s money. Separate from the debtor’s country.`}
                  label="Amount owed"
                />
              </span>
              <div className="amount-currency-control">
                <select
                  className="amount-currency-code"
                  aria-label="Currency"
                  value={form.currency}
                  onChange={(e) => update('currency', e.target.value)}
                  required
                >
                  {MATTER_CURRENCIES.map((item) => (
                    <option key={item.value} value={item.value} title={item.label}>
                      {item.value}
                    </option>
                  ))}
                </select>
                <input
                  className="amount-currency-value"
                  required
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-label="Amount owed"
                  value={formatCurrencyInputDisplay(form.amountOwed, form.currency, {
                    withSymbol: false,
                  })}
                  onChange={(e) =>
                    update('amountOwed', parseCurrencyInputValue(e.target.value))
                  }
                  onBlur={() => {
                    const committed = commitCurrencyAmount(form.amountOwed);
                    if (!committed) return;
                    update('amountOwed', committed);
                  }}
                  placeholder={`${currencyDisplaySymbol(form.currency)}0.00`}
                />
              </div>
            </label>
            <label className="field">
              <span>Invoice ID</span>
              <input
                value={form.externalInvoiceId}
                onChange={(e) => update('externalInvoiceId', e.target.value)}
                placeholder="If known"
              />
            </label>
            <label className="field">
              <span className="field-label-with-tip">
                Account #
                <FieldTip
                  text="Your ID for this customer in your system (not the invoice number)."
                  label="Account #"
                />
              </span>
              <input
                value={form.externalAccountNumber}
                onChange={(e) => update('externalAccountNumber', e.target.value)}
                placeholder="If known"
              />
            </label>
            <label className="field">
              <span className="field-label-with-tip">
                Will you accept a payment plan?
                <FieldTip text={PAYMENT_PLAN_HINT} label="Payment plan" />
              </span>
              <select
                value={form.installmentSelection}
                onChange={(e) => update('installmentSelection', e.target.value)}
              >
                {planOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field-label-with-tip">
                Will you consider a settlement offer?
                <FieldTip text={SETTLEMENT_CHOICE_HINT} label="Settlement" />
              </span>
              <select
                value={form.settlementChoice}
                onChange={(e) => update('settlementChoice', e.target.value)}
              >
                {SETTLEMENT_CHOICES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="field field-span-2">
              <span>Dispute type</span>
              <DisputeTypeSelect
                value={form.disputeType}
                onChange={(next) => update('disputeType', next)}
                required
              />
            </div>
            <EvidenceUpload
              fileNames={form.evidenceFileNames}
              files={evidenceFiles}
              onFilesSelected={onEvidenceFiles}
              onRemoveFile={removeEvidenceFile}
              ownerId={INTAKE_EVIDENCE_OWNER}
              vaultUrl={pendingEvidenceUrl(form.evidenceFileNames) || ''}
              storageBlocked={Boolean(vaultUsage?.atLimit)}
              storageMessage={
                vaultUsage?.atLimit ? evidenceVaultLimitMessage(vaultUsage) : ''
              }
              storageWarn={Boolean(vaultUsage?.atWarn && !vaultUsage?.atLimit)}
              storageWarnMessage={
                vaultUsage?.atWarn && !vaultUsage?.atLimit
                  ? evidenceVaultWarnMessage(vaultUsage)
                  : ''
              }
            />
          </div>

          {error ? <p className="form-error">{error}</p> : null}

          <div className="drawer-actions">
            <button type="button" className="btn-ghost" onClick={cancel}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={pending}>
              {pending ? 'Sending…' : 'Send Demand'}
            </button>
          </div>

          <p className="field-hint intake-charge-note">{demandFeeNote}</p>
        </form>
      </div>
    </div>
  );
}
